import {
  EventEmitter,
  Injectable,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import type { LngLatLike } from 'mapbox-gl';
import {
  CellGeoData,
  GeocoderCache,
} from '../../map/mapbox/services/geocoder-cache';
import { environment } from '../../../environments/environment';
import { ReportsService } from '../../reports/reports.service';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, Subject } from 'rxjs';
import { JsonResponse } from '../api/backend-config';
import { finalize } from 'rxjs/operators';
import { geocoderContextToString } from '../util/geocoder-context-to-string';
import { FeatureCollection } from 'geojson';
import { UserAccessService } from '../../user/access/user-access.service';
import { MapBoxService, TypeOfMap } from '../../map/mapbox/mapbox.service';
import { RadiusSelectionService } from '../../reports/order-report/choose-location/choose-location-map/radius-selection.service';
import {
  COUNTY_LEVEL_LAYER,
  H3_RES7_LEVEL_LAYER,
  LayerStoreService,
} from '../../map/mapbox/services/layer-store.service';
import { BreakpointObserverService } from './breakpoint-observer.service';
import { UserInterestLocationService } from './user-interest-location.service';

declare const mapboxgl: typeof import('mapbox-gl');
declare const MapboxGeocoder: typeof import('@mapbox/mapbox-gl-geocoder');

export type GeocoderSearchHistory = {
  id: string;
  address: string;
  lng: number;
  lat: number;
};

@Injectable({
  providedIn: 'root',
})
export class SearchGeocoderService {
  public readonly geocoderCache = new GeocoderCache();

  public readonly reverseGeocoder: MapboxGeocoder = new MapboxGeocoder({
    accessToken: environment.mapbox.accessToken,
    mapboxgl: mapboxgl,
    countries: 'us',
    language: 'en',
    reverseGeocode: true,
  });

  public searchHistoryList: GeocoderSearchHistory[] = [];

  public isListVisible: boolean = false;

  readonly requestFocus$ = new Subject<void>();

  public marker = new mapboxgl.Marker({ color: 'red' });
  // Save last search result coords to use instead of marker coords
  public lastSearchedCoordinates: [number, number] | null = null;

  private isSearchOpenedFirstTime = true;

  private inputEventListeners!: {
    handleClick: () => void;
    handleInput: () => void;
    handleBlur: () => void;
    handleControlButtons: (event: Event) => void;
  };

  private _clearListener?: () => void;
  private _resultListener?: (e: any) => void;

  // Suppress auto clear on geocoder detachment
  private suppressClear = false;

  private renderer: Renderer2;

  constructor(
    private reportsService: ReportsService,
    private userService: UserAccessService,
    rendererFactory: RendererFactory2,
    private http: HttpClient,
    private radiusSelectionService: RadiusSelectionService,
    private mapboxService: MapBoxService,
    private layerStore: LayerStoreService,
    private breakpointObserver: BreakpointObserverService,
    private userInterestLocationService: UserInterestLocationService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public addGeocoderSearch(
    map: mapboxgl.Map,
    searchGeocoder: MapboxGeocoder,
    searchClose: EventEmitter<void>,
    type?: TypeOfMap,
    popup?: mapboxgl.Popup
  ): void {
    if (!map.hasControl(searchGeocoder)) {
      this.suppressClear = true;

      const position = 'top-left';
      map.addControl(searchGeocoder, position);

      this._resultListener = (e) => this.handleSearchResult(map, e, type);
      searchGeocoder.on('result', this._resultListener);

      this._clearListener = () =>
        this.handleSearchClear(searchGeocoder, searchClose);
      searchGeocoder.on('clear', this._clearListener);

      this.handleInputEvents(searchGeocoder, popup);
    }
    // autofocus input after click
    // @ts-ignore
    searchGeocoder._inputEl.focus();
    this.requestFocus$.subscribe((focus) => {
      // @ts-ignore
      // may be bug-friendly singe there's single service but a few different geocoders
      searchGeocoder._inputEl.focus();
    });

    // Somehow icon replacement doesnt work on first render so here we artificially remove first geocoder and add new one
    if (this.isSearchOpenedFirstTime) {
      this.isSearchOpenedFirstTime = false;

      this.removeGeocoderSearch(map, searchGeocoder);

      setTimeout(() => {
        this.addGeocoderSearch(map, searchGeocoder, searchClose, type, popup);
      }, 0);
    }

    // Add async wrapper to avoid races
    queueMicrotask(() => {
      this.suppressClear = false;
    });
  }

  public flyToAddress(
    point: LngLatLike,
    map: mapboxgl.Map,
    zoom?: number
  ): void {
    const targetZoom = zoom ?? this.getSearchResultZoom();
    map.flyTo({ center: point, zoom: targetZoom });

    this.marker.setLngLat(point).addTo(map);

    const isMobileUnregistered =
      this.breakpointObserver.isMobile.getValue() &&
      !this.userService.getIsAuthorized();
    if (!isMobileUnregistered) {
      this.mapboxService.map.once('idle', () => {
        this.mapboxService.simulateMapClick(point as [number, number]);
      });
    }

    this.isListVisible = false;
  }

  public removeGeocoderSearch(
    map: mapboxgl.Map,
    searchGeocoder: MapboxGeocoder
  ): void {
    if (this._resultListener) {
      searchGeocoder.off('result', this._resultListener);
    }

    if (this._clearListener) {
      searchGeocoder.off('clear', this._clearListener);
    }

    if (this.inputEventListeners) {
      const inputEl = (searchGeocoder as any)._inputEl;
      const { handleClick, handleInput, handleBlur, handleControlButtons } =
        this.inputEventListeners;

      inputEl.removeEventListener('click', handleClick);
      inputEl.removeEventListener('input', handleInput);
      inputEl.removeEventListener('blur', handleBlur);
      inputEl.removeEventListener('keydown', handleControlButtons);
    }

    map.removeControl(searchGeocoder);
  }

  private handleInputEvents(searchGeocoder: any, popup?: mapboxgl.Popup): void {
    const inputEl = searchGeocoder._inputEl;

    const handleClick = () => {
      // On input click active map popup should be closed
      if (popup && popup.isOpen()) {
        popup.remove();
      }

      if (searchGeocoder.inputString === '') {
        this.isListVisible = true;
        if (this.userService.getIsAuthorized()) {
          this.updateSearchHistoryList(searchGeocoder);
        }
      }
    };

    const handleInput = () => {
      this.isListVisible = false;
    };

    const handleBlur = () => {
      // Disable input clear on blur, probably will use it again
      // searchGeocoder.setInput('');

      setTimeout(() => {
        const focusedElement = document.activeElement as HTMLElement;
        if (
          !focusedElement.closest('.search-history-wrapper') &&
          !focusedElement.closest('.history-search-item')
        ) {
          this.isListVisible = false;
        }
      }, 200); // Delay is needed to still handle click on history item before closing list
    };

    // Artificially set input to blank space so that it wont close upon ctrl, cmd, shift press
    const handleControlButtons = (event: any) => {
      if (
        (event.ctrlKey || event.shiftKey || event.metaKey) &&
        !event.key.match(/^[a-zA-Z0-9]$/)
      ) {
        if (searchGeocoder.inputString === '') {
          searchGeocoder.setInput(' ');
        }

        return;
      }
    };

    inputEl.addEventListener('click', handleClick);
    inputEl.addEventListener('input', handleInput);
    inputEl.addEventListener('blur', handleBlur);
    inputEl.addEventListener('keydown', handleControlButtons);

    // Store references for later removal
    this.inputEventListeners = {
      handleClick,
      handleInput,
      handleBlur,
      handleControlButtons,
    };
  }

  public removeSearchItemFromHistory(
    id: string,
    geocoder: MapboxGeocoder
  ): void {
    this.http
      .delete(environment.apiUrl + `geocoding/my?searchId=${id}`)
      .pipe(
        finalize(() => {
          this.updateSearchHistoryList(geocoder);
        })
      )
      .subscribe(),
      catchError((err) => {
        console.error('ERROR removing from search history', err);
        return of(null);
      });
  }

  public saveSearchResult(
    result: CellGeoData
  ): Observable<JsonResponse<CellGeoData>> {
    return this.http.put<JsonResponse<CellGeoData>>(
      environment.apiUrl + 'geocoding',
      {
        address: result.place_name,
        lng: result.center[0],
        lat: result.center[1],
      }
    );
  }

  private updateSearchHistoryList(geocoder: any): void {
    this.http
      .get<
        JsonResponse<GeocoderSearchHistory[]>
      >(environment.apiUrl + 'geocoding/my')
      .subscribe((data) => {
        this.searchHistoryList = data.object;
        geocoder._inputEl.focus();
      }),
      catchError((err) => {
        console.error('ERROR fetching search history', err);
        return of(null);
      });
  }

  private getSearchResultZoom(): number {
    // Mobile unregistered → county zoom only, to avoid sign-up popup (caused high bounce rate)
    const isMobileUnregistered =
      this.breakpointObserver.isMobile.getValue() &&
      !this.userService.getIsAuthorized();
    if (isMobileUnregistered) {
      // Mobile threshold for H3_RES_5 is zoom 6, so stay just below it
      return 5.99;
    }
    const hasRes7Access = this.userService
      .getAccessibleLevels()
      .includes(H3_RES7_LEVEL_LAYER);
    return hasRes7Access
      ? this.layerStore.getSufficientZoomByLevel(H3_RES7_LEVEL_LAYER)
      : this.layerStore.getSufficientZoomByLevel('H3_RES_5');
  }

  private handleSearchResult = (
    map: mapboxgl.Map,
    event: { result: CellGeoData },
    type?: TypeOfMap
  ): void => {
    this.marker.remove();
    const center = event.result.center;
    const ll = mapboxgl.LngLat.convert(center);

    this.saveSearchResult(event.result).subscribe(),
      catchError((err) => {
        console.error('ERROR saving search result', err);
        return of(null);
      });

    this.marker.setLngLat(center).addTo(map);
    this.lastSearchedCoordinates = center;
    this.userInterestLocationService.setExplicit(ll.lat, ll.lng);

    const targetZoom = this.getSearchResultZoom();
    map.flyTo({ center, zoom: targetZoom });

    const isMobileUnregistered =
      this.breakpointObserver.isMobile.getValue() &&
      !this.userService.getIsAuthorized();
    if (isMobileUnregistered) {
      return;
    }

    if (type === 'default') {
      map.once('idle', () => {
        this.mapboxService.simulateMapClick(center);
      });
    }

    if (type === 'report') {
      // Instead of using simulateMapClick(), simulate feature creation without click
      this.mapboxService.removeAllMarkers(type);
      this.reportsService.deselectReportsFeature();
      this.radiusSelectionService.renderCircle(ll);
      this.reportsService.isLocationSelected = true;
      this.queryReverseGeocoder(ll);
    }
  };

  private handleSearchClear = (
    searchGeocoder: MapboxGeocoder,
    searchClose: EventEmitter<void>
  ): void => {
    if (!this.suppressClear) {
      this.lastSearchedCoordinates = null;
    }

    // Clear event is being fired twice, so we needed to workaround with separate string variable that would store input
    this.marker.remove();
    this.isListVisible = false;

    // @ts-ignore
    const inputString = searchGeocoder.inputString.trim();

    if (!inputString || inputString.length === 0) {
      searchClose.emit();
    } else {
      // @ts-ignore
      searchGeocoder.inputString = '';
    }
  };

  public queryReverseGeocoder(coordinates: mapboxgl.LngLat): void {
    this.handleGeocoderRequest(coordinates).then((res) => {
      // If we'll need more info on address like postal code, number of building etc. we can use res.place_name
      this.reportsService.selectedLocation = geocoderContextToString(res);
    });
  }

  private handleGeocoderResult(): Promise<CellGeoData> {
    return new Promise((res) => {
      const resultHandler = (event: any) => {
        this.reverseGeocoder.off('result', resultHandler);
        res(event.result);
      };

      this.reverseGeocoder.on('result', resultHandler);
    });
  }

  private async handleGeocoderRequest(
    coordinates: mapboxgl.LngLat
  ): Promise<CellGeoData> {
    this.reverseGeocoder.query(`${coordinates.lat},${coordinates.lng}`);

    return await this.handleGeocoderResult();
  }

  public async forwardGeocode(input: string): Promise<any> {
    const searchText = input;

    const url = `https://api.mapbox.com/search/geocode/v6/forward?q=${searchText}&autocomplete=${true}&country=US&access_token=${environment.mapbox.accessToken}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error('Forward geocoding failed:', error);
      throw error;
    }
  }

  public async reverseGeocode(
    coordinates: mapboxgl.LngLat
  ): Promise<FeatureCollection> {
    const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${coordinates.lng}&latitude=${coordinates.lat}&access_token=${environment.mapbox.accessToken}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      throw error;
    }
  }
}
